import { useEffect, useRef, useState } from 'react'
import { lightTheme } from '@/customTheme'
import { useTranslation } from 'react-i18next'
import HeadingInspector from '@/components/HeadingInspector'
import { useStyletron } from 'styletron-react'
import ButtonCustom from '@/components/ButtonCustom'
import { SizeButton } from '@/constants/sizeButton'
import { SignInManager } from '../../SignInManager'
import { UserInfo } from '@/common/interfaces'
import Icons from '../../../../Icons'
import { isValidEmail, getInputStyles, errorMessageStyles } from '../signInStyles'
import SignInWithEmailVerifyCode from '../SignInWithEmail/SignInWithEmailVerifyCode'

interface SignUpWithPasswordScreenProps {
  onBack: () => void
  onSuccess: (user: UserInfo) => void
  onSignIn?: () => void
}

function SignUpWithPasswordScreen({ onBack, onSuccess, onSignIn }: SignUpWithPasswordScreenProps) {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [showVerifyCode, setShowVerifyCode] = useState(false)
  const [css] = useStyletron()

  const emailInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    emailInputRef.current?.focus()
  }, [])

  const clearError = () => error && setError('')

  const handleSignUp = async () => {
    if (!isValidEmail(email)) {
      setError('Please enter a valid email address.')
      return
    }

    if (!password) {
      setError('Please enter your password.')
      return
    }

    setIsSubmitting(true)
    setError('')
    try {
      await SignInManager.getInstance().signUpWithEmailAndPassword(email, password)
      setShowVerifyCode(true)
    } catch (err) {
      console.error('Sign up error:', err)
      setError('Something went wrong. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const isDisabled = !email || !password || !!error
  const inputStyles = getInputStyles({ hasError: !!error, isDisabled: isSubmitting })

  if (showVerifyCode) {
    return (
      <SignInWithEmailVerifyCode
        email={email}
        onBack={() => setShowVerifyCode(false)}
        onSuccess={onSuccess}
      />
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <HeadingInspector
        hasNavigation
        handleNavigation={onBack}
        title={t('Sign up with Email & Password')}
        style={{ padding: 0 }}
      />
      <h3
        style={{
          marginBottom: 0,
          marginTop: '8px',
          ...lightTheme.typography.Small14regular,
          fontWeight: 400,
          color: lightTheme.colors.blackGray,
        }}
      >
        {t('Create your account to continue.')}
      </h3>

      <input
        ref={emailInputRef}
        type="email"
        value={email}
        onChange={e => {
          setEmail(e.target.value)
          clearError()
        }}
        placeholder={t('Enter your email address')}
        disabled={isSubmitting}
        className={css({ ...inputStyles, marginTop: '32px', marginBottom: '16px' } as any)}
        onKeyPress={e => e.key === 'Enter' && !isDisabled && handleSignUp()}
      />

      <div style={{ position: 'relative', width: '100%' }}>
        <input
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={e => {
            setPassword(e.target.value)
            clearError()
          }}
          placeholder={t('Enter your password')}
          disabled={isSubmitting}
          className={css({ ...inputStyles, marginBottom: error ? '0' : '24px', paddingRight: '48px' } as any)}
          onKeyPress={e => e.key === 'Enter' && !isDisabled && handleSignUp()}
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          disabled={isSubmitting}
          style={{
            position: 'absolute',
            right: '12px',
            top: '24px',
            transform: 'translateY(-50%)',
            background: 'transparent',
            border: 'none',
            cursor: isSubmitting ? 'not-allowed' : 'pointer',
            padding: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: isSubmitting ? 0.5 : 1,
          }}
          title={showPassword ? t('Hide password') : t('Show password')}
        >
          {showPassword ? <Icons.EyeShowIcon size={20} /> : <Icons.EyeHideIcon size={20} />}
        </button>
      </div>

      {error && <p style={errorMessageStyles}>{t(error)}</p>}

      <ButtonCustom
        kind="primary"
        type={SizeButton.LARGE}
        onClick={handleSignUp}
        disabled={isDisabled}
        isLoading={isSubmitting}
        style={isSubmitting ? { background: `${lightTheme.colors.primary}!important` } : {}}
      >
        {t('Sign Up')}
      </ButtonCustom>

      <h4
        style={{
          marginBottom: 0,
          marginTop: '8px',
          ...lightTheme.typography.Small12medium,
          color: lightTheme.colors.grayScale400,
          fontWeight: 400,
          textAlign: 'center',
        }}
      >
        {t("We'll send a one-time code by email to verify your account.")}
      </h4>

      {onSignIn && (
        <h4
          style={{
            marginBottom: 0,
            marginTop: '24px',
            ...lightTheme.typography.Small12medium,
            color: lightTheme.colors.grayScale400,
            fontWeight: 400,
            textAlign: 'center',
            cursor: isSubmitting ? 'not-allowed' : 'pointer',
            opacity: isSubmitting ? 0.5 : 1,
          }}
          onClick={() => {
            if (isSubmitting) return
            onSignIn()
          }}
        >
          {t('Already have an account? Sign in')}
        </h4>
      )}
    </div>
  )
}

export default SignUpWithPasswordScreen
